import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { posApi } from '../api/pos.api';
import { api } from '../services/api';
import { BackroomProduct, ProductLot } from '../types/lots.types';
import { Product } from '../types/pos.types';
import { formatQty } from '../utils/pos.utils';
import { Package, TrendingUp, Calendar, User, Factory, BadgeCheck, AlertCircle, Edit, FileText, Image, Camera, MapPin } from 'lucide-react';
import ProductEditModal from '../components/ProductEditModal';
import WebcamCaptureModal from '../components/ui/WebcamCaptureModal';
import BarcodeScannerModal from '../components/ui/BarcodeScannerModal';
import { useBarcodeScan } from '../hooks/useBarcodeScan';

export default function BackroomManagement() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [products, setProducts] = useState<BackroomProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [movingLot, setMovingLot] = useState<string | null>(null);
  const [moveQuantities, setMoveQuantities] = useState<Record<string, number>>({});
  const [searchTerm, setSearchTerm] = useState('');

  // État pour le modal d'édition de produit
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // État pour la capture photo étiquette
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [webcamLotId, setWebcamLotId] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState<string | null>(null);

  // État pour la preview photo
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null);

  // État pour le scanner caméra (iPad)
  const [scannerOpen, setScannerOpen] = useState(false);

  // Scan global code-barres → remplit le champ de recherche
  useBarcodeScan({
    onScan: (barcode) => {
      setSearchTerm(barcode);
    },
  });

  useEffect(() => {
    if (tenant?.id) {
      loadBackroomLots();
    }
  }, [tenant?.id]);

  const loadBackroomLots = async () => {
    try {
      setLoading(true);
      const data = await posApi.getBackroomLots();
      setProducts(data);

      // Initialiser les quantités avec le stock disponible
      const initialQuantities: Record<string, number> = {};
      data.forEach((productGroup: BackroomProduct) => {
        productGroup.lots.forEach((lot: ProductLot) => {
          initialQuantities[lot.id] = lot.stock_backroom;
        });
      });
      setMoveQuantities(initialQuantities);
    } catch (err: any) {
      console.error('Failed to load backroom lots:', err);
      alert(err.message || 'Échec du chargement des lots en réserve');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveToDisplay = async (lotId: string, productId: string) => {
    const quantity = moveQuantities[lotId] || 0;

    if (quantity <= 0) {
      alert('La quantité doit être supérieure à 0');
      return;
    }

    try {
      setMovingLot(lotId);
      const result = await posApi.moveToDisplay(lotId, quantity);

      // Recharger la liste
      loadBackroomLots();

      // Charger le produit complet et ouvrir le modal d'édition
      try {
        const productData = await api.getProduct(productId);
        setSelectedProduct(productData);
        setEditModalOpen(true);
      } catch (err) {
        console.error('Erreur chargement produit:', err);
        // Si erreur de chargement du produit, afficher simplement le message de succès
        if (result.productActivated) {
          alert(`✅ ${quantity} unités déplacées vers le rayon.\n🎉 Le produit a été activé!`);
        } else {
          alert(`✅ ${quantity} unités déplacées vers le rayon avec succès`);
        }
      }
    } catch (err: any) {
      console.error('Failed to move to display:', err);
      alert(err.message || 'Échec du déplacement vers le rayon');
    } finally {
      setMovingLot(null);
    }
  };

  // Fonction pour ouvrir le modal d'édition pour un produit
  const openEditModal = async (productId: string) => {
    try {
      const productData = await api.getProduct(productId);
      setSelectedProduct(productData);
      setEditModalOpen(true);
    } catch (err) {
      console.error('Erreur chargement produit:', err);
      alert('Erreur lors du chargement du produit');
    }
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedProduct(null);
  };

  const handleEditModalSave = () => {
    loadBackroomLots(); // Recharger les données après modification
  };

  // Ouvrir la webcam pour capturer une photo d'étiquette
  const openWebcamForLot = (lotId: string) => {
    setWebcamLotId(lotId);
    setWebcamOpen(true);
  };

  // Uploader la photo capturée
  const handleLabelPhotoCapture = async (file: File) => {
    if (!webcamLotId) return;

    try {
      setUploadingPhoto(webcamLotId);
      await posApi.uploadLotLabelPhoto(webcamLotId, file);
      loadBackroomLots();
    } catch (err: any) {
      console.error('Failed to upload label photo:', err);
      alert(err.message || 'Erreur lors de l\'upload de la photo');
    } finally {
      setUploadingPhoto(null);
      setWebcamLotId(null);
    }
  };

  const updateQuantity = (lotId: string, value: number) => {
    const lot = products
      .flatMap(p => p.lots)
      .find(l => l.id === lotId);

    if (!lot) return;

    // Limiter la quantité au stock disponible
    const maxQuantity = lot.stock_backroom;
    const newQuantity = Math.max(0, Math.min(value, maxQuantity));

    setMoveQuantities(prev => ({
      ...prev,
      [lotId]: newQuantity,
    }));
  };

  const toggleProduct = (productId: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const formatPrice = (cents?: number | null) => {
    if (cents === null || cents === undefined) return '-';
    return `${(cents / 100).toFixed(2)} €`;
  };

  const isExpiringSoon = (expiryDate?: string | null) => {
    if (!expiryDate) return false;
    const daysUntilExpiry = Math.ceil((new Date(expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate?: string | null) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const receptionTime = (dateStr?: string | null) =>
    dateStr ? new Date(dateStr).getTime() : 0;

  const maxReceptionTime = (lots: ProductLot[]) =>
    lots.reduce((max, lot) => Math.max(max, receptionTime(lot.reception_date)), 0);

  const filteredProducts = products
    .filter(productGroup => {
      const searchLower = searchTerm.toLowerCase();
      return (
        productGroup.product.name.toLowerCase().includes(searchLower) ||
        productGroup.product.code?.toLowerCase().includes(searchLower) ||
        productGroup.product.barcode?.toLowerCase().includes(searchLower) ||
        productGroup.lots.some(lot =>
          lot.lot_number.toLowerCase().includes(searchLower) ||
          lot.supplier_name?.toLowerCase().includes(searchLower) ||
          lot.producer_name?.toLowerCase().includes(searchLower)
        )
      );
    })
    .map(productGroup => ({
      ...productGroup,
      lots: [...productGroup.lots].sort((a, b) => receptionTime(b.reception_date) - receptionTime(a.reception_date)),
    }))
    .sort((a, b) => maxReceptionTime(b.lots) - maxReceptionTime(a.lots));

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Gestion de la Réserve</h1>
        <p className="text-gray-600">
          Gérez vos lots de produits en réserve et transférez-les vers le rayon
        </p>
      </div>

      {/* Barre de recherche */}
      <div className="mb-6 relative">
        <input
          type="text"
          placeholder="Rechercher par nom, code, code-barres, lot, fournisseur ou producteur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-3 pr-14 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          type="button"
          onClick={() => setScannerOpen(true)}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          aria-label="Scanner via caméra"
          title="Scanner via caméra"
        >
          <Camera className="w-5 h-5" />
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Produits en réserve</p>
              <p className="text-2xl font-bold text-blue-900">{products.length}</p>
            </div>
            <Package className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total lots</p>
              <p className="text-2xl font-bold text-green-900">
                {products.reduce((sum, p) => sum + p.lots.length, 0)}
              </p>
            </div>
            <Package className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Nouveaux produits</p>
              <p className="text-2xl font-bold text-purple-900">
                {products.filter(p => !p.product.active).length}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Liste des produits */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Aucun produit en réserve</p>
          <p className="text-gray-500 text-sm mt-2">
            Les produits apparaîtront ici après application d'une livraison
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProducts.map((productGroup) => {
            const isExpanded = expandedProducts.has(productGroup.product.id);

            return (
              <div key={productGroup.product.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                {/* En-tête produit */}
                <div
                  onClick={() => toggleProduct(productGroup.product.id)}
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{productGroup.product.name}</h3>
                        {!productGroup.product.active && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                            ⭐ Nouveau produit
                          </span>
                        )}
                        {productGroup.lots.some(lot => isExpiringSoon(lot.expiry_date)) && (
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Expire bientôt
                          </span>
                        )}
                        {/* Bouton d'édition du produit */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(productGroup.product.id);
                          }}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Modifier le produit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex gap-6 text-sm text-gray-600">
                        {productGroup.product.code && (
                          <span>Code: {productGroup.product.code}</span>
                        )}
                        {productGroup.product.barcode && (
                          <span>EAN: {productGroup.product.barcode}</span>
                        )}
                        {productGroup.product.category && (
                          <span>Catégorie: {productGroup.product.category.full_path || productGroup.product.category.label}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600 mb-1">{productGroup.lots.length} lot(s)</div>
                      <div className="text-lg font-bold text-blue-600">
                        {productGroup.total_backroom.toFixed(2)} {productGroup.lots[0]?.unit || 'unités'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {isExpanded ? '▲ Masquer' : '▼ Voir les lots'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Liste des lots (expandable) */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50">
                    <div className="p-4 space-y-3">
                      {productGroup.lots.map((lot) => (
                        <div
                          key={lot.id}
                          className={`bg-white border rounded-lg p-4 ${
                            isExpired(lot.expiry_date)
                              ? 'border-red-300 bg-red-50'
                              : isExpiringSoon(lot.expiry_date)
                              ? 'border-orange-300 bg-orange-50'
                              : 'border-gray-200'
                          }`}
                        >
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Informations du lot */}
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">Lot {lot.lot_number}</span>
                                {lot.supplier_lot_number && (
                                  <span className="text-sm text-gray-600">
                                    (Fournisseur: {lot.supplier_lot_number})
                                  </span>
                                )}
                              </div>

                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Calendar className="w-4 h-4" />
                                  <span>Réception: {formatDate(lot.reception_date)}</span>
                                </div>
                                {lot.expiry_date && (
                                  <div className={`flex items-center gap-2 ${
                                    isExpired(lot.expiry_date)
                                      ? 'text-red-600 font-semibold'
                                      : isExpiringSoon(lot.expiry_date)
                                      ? 'text-orange-600 font-semibold'
                                      : 'text-gray-600'
                                  }`}>
                                    <Calendar className="w-4 h-4" />
                                    <span>DLC: {formatDate(lot.expiry_date)}</span>
                                  </div>
                                )}
                                {lot.supplier_name && (
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <Factory className="w-4 h-4" />
                                    <span>{lot.supplier_name}</span>
                                  </div>
                                )}
                                {lot.producer_name && (
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <User className="w-4 h-4" />
                                    <span>{lot.producer_name}</span>
                                  </div>
                                )}
                                {lot.organic_certifier && (
                                  <div className="flex items-center gap-2 text-green-700">
                                    <BadgeCheck className="w-4 h-4" />
                                    <span>{lot.organic_certifier}</span>
                                  </div>
                                )}
                              </div>

                              <div className="flex gap-4 text-sm pt-2 border-t">
                                {lot.purchase_price_cents !== null && lot.purchase_price_cents !== undefined && (
                                  <div>
                                    <span className="text-gray-600">Prix d'achat: </span>
                                    <span className="font-semibold">{formatPrice(lot.purchase_price_cents)}</span>
                                  </div>
                                )}
                                {lot.suggested_price_cents !== null && lot.suggested_price_cents !== undefined && (
                                  <div>
                                    <span className="text-gray-600">Prix conseillé: </span>
                                    <span className="font-semibold text-green-700">{formatPrice(lot.suggested_price_cents)}</span>
                                  </div>
                                )}
                              </div>

                              {/* Traçabilité : document source + photo étiquette */}
                              <div className="flex items-center gap-2 pt-2 border-t">
                                {lot.source_document_url && (
                                  <a
                                    href={lot.source_document_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                                    title="Voir le bon de livraison / facture"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <FileText className="w-3.5 h-3.5" />
                                    <span>Document</span>
                                  </a>
                                )}
                                {lot.label_photo_url && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewPhotoUrl(lot.label_photo_url!);
                                    }}
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors"
                                    title="Voir la photo de l'étiquette"
                                  >
                                    <Image className="w-3.5 h-3.5" />
                                    <span>Étiquette</span>
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openWebcamForLot(lot.id);
                                  }}
                                  disabled={uploadingPhoto === lot.id}
                                  className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                                  title={lot.label_photo_url ? 'Remplacer la photo' : 'Prendre une photo de l\'étiquette'}
                                >
                                  <Camera className="w-3.5 h-3.5" />
                                  <span>{uploadingPhoto === lot.id ? 'Upload...' : lot.label_photo_url ? 'Remplacer' : 'Photo'}</span>
                                </button>
                              </div>
                            </div>

                            {/* Contrôles de transfert */}
                            <div className="flex flex-col justify-between">
                              <div className="mb-3">
                                <div className="text-sm text-gray-600 mb-1">Stock en réserve</div>
                                <div className="text-2xl font-bold text-blue-600">
                                  {formatQty(lot.stock_backroom)} {lot.unit || 'unités'}
                                </div>
                                {lot.stock_display > 0 && (
                                  <div className="text-sm text-gray-500">
                                    (En rayon: {formatQty(lot.stock_display)} {lot.unit || 'unités'})
                                  </div>
                                )}
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => updateQuantity(lot.id, (moveQuantities[lot.id] || 0) - 1)}
                                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-bold"
                                    disabled={movingLot === lot.id}
                                  >
                                    -
                                  </button>
                                  <input
                                    type="number"
                                    value={moveQuantities[lot.id] || 0}
                                    onChange={(e) => updateQuantity(lot.id, parseFloat(e.target.value) || 0)}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-center"
                                    min="0"
                                    max={lot.stock_backroom}
                                    step="0.01"
                                    disabled={movingLot === lot.id}
                                  />
                                  <button
                                    onClick={() => updateQuantity(lot.id, (moveQuantities[lot.id] || 0) + 1)}
                                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-bold"
                                    disabled={movingLot === lot.id}
                                  >
                                    +
                                  </button>
                                </div>

                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleMoveToDisplay(lot.id, productGroup.product.id)}
                                    disabled={movingLot === lot.id || (moveQuantities[lot.id] || 0) <= 0}
                                    className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
                                      movingLot === lot.id
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : (moveQuantities[lot.id] || 0) <= 0
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-green-600 hover:bg-green-700 text-white'
                                    }`}
                                  >
                                    {movingLot === lot.id ? 'Transfert...' : 'Mettre en rayon'}
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/storage/putaway?lotId=${lot.id}`);
                                    }}
                                    className="py-2 px-3 rounded-lg font-semibold transition-colors bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1"
                                    title="Ranger en emplacement"
                                  >
                                    <MapPin className="w-4 h-4" />
                                    Ranger
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal d'édition de produit */}
      <ProductEditModal
        product={selectedProduct}
        isOpen={editModalOpen}
        onClose={handleEditModalClose}
        onSave={handleEditModalSave}
      />

      {/* Modal webcam pour photo étiquette */}
      <WebcamCaptureModal
        isOpen={webcamOpen}
        onClose={() => {
          setWebcamOpen(false);
          setWebcamLotId(null);
        }}
        onCapture={handleLabelPhotoCapture}
      />

      {/* Scanner code-barres caméra */}
      <BarcodeScannerModal
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={(code) => setSearchTerm(code)}
        title="Scanner un code-barres"
      />

      {/* Modal preview photo étiquette */}
      {previewPhotoUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewPhotoUrl(null)}
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewPhotoUrl(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-sm flex items-center gap-1"
            >
              Fermer
            </button>
            <img
              src={previewPhotoUrl}
              alt="Photo étiquette"
              className="w-full rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}
